/**
 * Agregace „zadané práce“ pro statistiky uživatele (assignee).
 * Termín zadání = `endsAt` (konec plánovaného okna), jinak `startsAt`.
 * Splněno = Fronta Hotovo pro daný work kind, nebo `status === 'done'`.
 */

/** České vysvětlení KPI karty Zadané práce (statistiky uživatele). */
export const ASSIGNED_WORK_KPI_INFO =
  'Splněné zadané práce / všechny zadané práce ve zvoleném období od–do. „V termínu“ znamená, že práce byla hotová nejpozději v den termínu. Kliknutím otevřete detail s kartami a grafem růstu.';

/** České vysvětlení detailních karet Zadané práce. */
export const ASSIGNED_WORK_DETAIL_COMPLETED_INFO =
  'Počet splněných zadaných prací vůči všem zadaným ve zvoleném období od–do.';

export const ASSIGNED_WORK_DETAIL_ON_TIME_INFO =
  'Počet zadaných prací, které byly hotové nejpozději v den termínu.';

import {
  parseBudovaMilestonesFromMetadata,
  parseBudovaMilestonesMeta,
  type BudovaMilestoneId,
} from '../constants/object-workflow/budova-milestone-engine';
import { PHASE_CODE } from '../constants/object-workflow/phase-codes';
import {
  type WorkReservationKind,
  type WorkReservationStatus,
  workQueueTabForReservationKind,
} from '../constants/work-reservation.constants';
import { hasReachedWorkflowMilestone } from '../constants/object-workflow/workflow-model';
import {
  isWorkQueueBuildingMilestoneComplete,
  isWorkQueueRozvadecMilestoneComplete,
  type WorkQueueBuildingInput,
  type WorkQueueRozvadecInput,
} from './work-queue.util';

export type AssignedWorkStatsRow = {
  id: string;
  workKind: WorkReservationKind;
  status: WorkReservationStatus;
  objectId: string | null;
  objectType?: string | null;
  objectStatus?: string | null;
  /** `objects.metadata` (milníky / completedAt). */
  objectMetadata?: Record<string, unknown> | null;
  digitalniCesta?: number | null;
  unifiedHpCount?: number | null;
  budovaBuildingType?: string | null;
  statusAcq?: string | null;
  assignedAt: string | null;
  startsAt: string | null;
  endsAt: string | null;
  updatedAt: string | null;
  /**
   * Nejdříví přechod fáze na cílový milník po přiřazení (ISO),
   * doplněný z `object_history` pro phase-based kindy.
   */
  phaseCompletedAt?: string | null;
};

export type AssignedWorkStatsSummary = {
  /** Zadaných (necancelled) v období. */
  assignedCount: number;
  /** Splněno (Hotovo Fronta / status done). */
  completedCount: number;
  /** Splněno v termínu zadání (completionAt ≤ deadline). */
  completedOnTimeCount: number;
};

export type AssignedWorkStatsItem = AssignedWorkStatsRow & {
  completed: boolean;
  completedOnTime: boolean;
  deadlineAt: string | null;
  completedAt: string | null;
};

export type AssignedWorkStatsResult = AssignedWorkStatsSummary & {
  items: AssignedWorkStatsItem[];
};

/** Termín zadání: konec okna, jinak začátek (Fronta / Navolávání). */
export function assignmentDeadlineIso(
  startsAt: string | null | undefined,
  endsAt: string | null | undefined,
): string | null {
  const end = String(endsAt ?? '').trim();
  if (end) {
    const t = Date.parse(end);
    if (Number.isFinite(t)) return new Date(t).toISOString();
  }
  const start = String(startsAt ?? '').trim();
  if (start) {
    const t = Date.parse(start);
    if (Number.isFinite(t)) return new Date(t).toISOString();
  }
  return null;
}

/** Milník s `completedAt` auditem pro kind (null = fáze / status). */
export function budovaMilestoneIdForWorkKind(
  kind: WorkReservationKind,
): BudovaMilestoneId | null {
  switch (kind) {
    case 'zavrt':
      return 'ZAVRTANY';
    case 'odboceni':
      return 'ODBOCENO';
    case 'vr':
    case 'digitalni_cesta':
      return 'ZRIZENE_VR';
    default:
      return null;
  }
}

/**
 * Cílové phase kódy pro historii (phase-based kindy).
 * Prázdné = milník / done-only.
 */
export function targetPhaseCodesForWorkKind(kind: WorkReservationKind): string[] {
  switch (kind) {
    case 'zafuk':
      return [PHASE_CODE.BUDOVA_ZAFOUKNUTO];
    case 'svar_budovy':
      return [PHASE_CODE.BUDOVA_SVARENY, PHASE_CODE.BUDOVA_ZAFOUKNUTO];
    case 'rozvadec_postaveni':
      return [PHASE_CODE.ROZVADEC_POSTAVENY];
    case 'rozvadec_zafuk':
      return [PHASE_CODE.ROZVADEC_ZAFOUKNUTO_SLOUPEK];
    case 'rozvadec_provareni':
      return [PHASE_CODE.ROZVADEC_PROVARENE];
    default:
      return [];
  }
}

export function isAssignedWorkCompleted(row: AssignedWorkStatsRow): boolean {
  if (row.status === 'done') return true;
  if (row.status === 'cancelled') return false;
  const objectId = (row.objectId ?? '').trim();
  if (!objectId) return false;

  const tab = workQueueTabForReservationKind(row.workKind) as
    | Parameters<typeof isWorkQueueBuildingMilestoneComplete>[1]
    | Parameters<typeof isWorkQueueRozvadecMilestoneComplete>[1];
  const type = String(row.objectType ?? '').trim().toUpperCase();
  const milestones = parseBudovaMilestonesFromMetadata(row.objectMetadata ?? null);

  if (type === 'ROZVADEC') {
    const input: WorkQueueRozvadecInput = { id: objectId, status: row.objectStatus };
    return isWorkQueueRozvadecMilestoneComplete(input, tab);
  }

  const input: WorkQueueBuildingInput = {
    id: objectId,
    status: row.objectStatus,
    statusAcq: row.statusAcq,
    digitalniCesta: row.digitalniCesta,
    unifiedHpCount: row.unifiedHpCount,
    budovaBuildingType: row.budovaBuildingType,
    budovaMilestones: milestones,
  };
  return isWorkQueueBuildingMilestoneComplete(input, tab);
}

export function resolveAssignedWorkCompletedAt(row: AssignedWorkStatsRow): string | null {
  if (row.status === 'done') {
    const updated = String(row.updatedAt ?? '').trim();
    if (updated && Number.isFinite(Date.parse(updated))) return new Date(updated).toISOString();
  }

  const milestoneId = budovaMilestoneIdForWorkKind(row.workKind);
  if (milestoneId) {
    const meta = parseBudovaMilestonesMeta(row.objectMetadata ?? null);
    const at = meta.completedAt[milestoneId]?.trim();
    if (at && Number.isFinite(Date.parse(at))) return new Date(at).toISOString();
  }

  const phaseAt = String(row.phaseCompletedAt ?? '').trim();
  if (phaseAt && Number.isFinite(Date.parse(phaseAt))) return new Date(phaseAt).toISOString();

  return null;
}

export function isAssignedWorkCompletedOnTime(row: AssignedWorkStatsRow): boolean {
  if (!isAssignedWorkCompleted(row)) return false;
  const deadline = assignmentDeadlineIso(row.startsAt, row.endsAt);
  if (!deadline) return false;
  const completedAt = resolveAssignedWorkCompletedAt(row);
  if (!completedAt) return false;
  return Date.parse(completedAt) <= Date.parse(deadline);
}

/** Pure agregace — použito v API i unit testech. */
export function aggregateAssignedWorkStats(
  rows: ReadonlyArray<AssignedWorkStatsRow>,
): AssignedWorkStatsResult {
  const items: AssignedWorkStatsItem[] = [];
  let assignedCount = 0;
  let completedCount = 0;
  let completedOnTimeCount = 0;

  for (const row of rows) {
    if (row.status === 'cancelled') continue;
    assignedCount += 1;
    const completed = isAssignedWorkCompleted(row);
    const completedAt = completed ? resolveAssignedWorkCompletedAt(row) : null;
    const deadlineAt = assignmentDeadlineIso(row.startsAt, row.endsAt);
    const completedOnTime =
      completed &&
      !!deadlineAt &&
      !!completedAt &&
      Date.parse(completedAt) <= Date.parse(deadlineAt);
    if (completed) completedCount += 1;
    if (completedOnTime) completedOnTimeCount += 1;
    items.push({
      ...row,
      completed,
      completedOnTime,
      deadlineAt,
      completedAt,
    });
  }

  return { assignedCount, completedCount, completedOnTimeCount, items };
}

/** @deprecated unused helper kept for clarity in specs — prefer hasReachedPhase via work-queue. */
export function phaseReachedForAssignedWork(
  status: string | null | undefined,
  kind: WorkReservationKind,
): boolean {
  const codes = targetPhaseCodesForWorkKind(kind);
  if (!codes.length) return false;
  return codes.some((code) => hasReachedWorkflowMilestone(status, code));
}

export type AssignedWorkStatsBucket = 'day' | 'week' | 'month';

export type AssignedWorkStatsTimeseriesPoint = {
  /** UTC začátek bucketu (ISO). */
  bucket: string;
  assignedCount: number;
  completedCount: number;
  completedOnTimeCount: number;
};

export type AssignedWorkStatsTimeseriesResult = {
  bucket: AssignedWorkStatsBucket;
  from: string;
  to: string;
  points: AssignedWorkStatsTimeseriesPoint[];
};

type AssignedWorkTimeseriesItem = Pick<
  AssignedWorkStatsItem,
  'assignedAt' | 'completed' | 'completedOnTime'
>;

function parseUtcDateOnlyMs(value: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? '').trim());
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Truncate UTC instant to bucket start (day / ISO week Mon / month). */
export function truncateAssignedWorkBucketStart(
  instantMs: number,
  bucket: AssignedWorkStatsBucket,
): number {
  const d = new Date(instantMs);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  if (bucket === 'day') return Date.UTC(y, m, day);
  if (bucket === 'month') return Date.UTC(y, m, 1);
  // ISO week: Monday start
  const dow = d.getUTCDay(); // 0=Sun … 6=Sat
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  return Date.UTC(y, m, day + mondayOffset);
}

function enumerateAssignedWorkBucketStarts(
  fromMs: number,
  toMsInclusive: number,
  bucket: AssignedWorkStatsBucket,
): number[] {
  const starts: number[] = [];
  let cur = truncateAssignedWorkBucketStart(fromMs, bucket);
  const end = truncateAssignedWorkBucketStart(toMsInclusive, bucket);
  while (cur <= end) {
    starts.push(cur);
    const d = new Date(cur);
    if (bucket === 'day') {
      cur = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1);
    } else if (bucket === 'week') {
      cur = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 7);
    } else {
      cur = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
    }
  }
  return starts;
}

/**
 * Růst Zadané práce v čase — počty podle `assignedAt` v bucketu
 * (stejný filtr období jako snapshot: zadání v Od–Do).
 */
export function buildAssignedWorkStatsTimeseries(params: {
  items: ReadonlyArray<AssignedWorkTimeseriesItem>;
  from: string;
  to: string;
  bucket: AssignedWorkStatsBucket;
  fillBuckets?: boolean;
}): AssignedWorkStatsTimeseriesResult {
  const bucket = params.bucket;
  const fromMs = parseUtcDateOnlyMs(params.from);
  const toMs = parseUtcDateOnlyMs(params.to);
  if (fromMs == null || toMs == null || fromMs > toMs) {
    return { bucket, from: params.from, to: params.to, points: [] };
  }

  const sparse = new Map<
    number,
    { assignedCount: number; completedCount: number; completedOnTimeCount: number }
  >();

  for (const item of params.items) {
    const assignedRaw = String(item.assignedAt ?? '').trim();
    if (!assignedRaw) continue;
    const assignedMs = Date.parse(assignedRaw);
    if (!Number.isFinite(assignedMs)) continue;
    const key = truncateAssignedWorkBucketStart(assignedMs, bucket);
    const row = sparse.get(key) ?? {
      assignedCount: 0,
      completedCount: 0,
      completedOnTimeCount: 0,
    };
    row.assignedCount += 1;
    if (item.completed) row.completedCount += 1;
    if (item.completedOnTime) row.completedOnTimeCount += 1;
    sparse.set(key, row);
  }

  const emptyMetrics = (): Omit<AssignedWorkStatsTimeseriesPoint, 'bucket'> => ({
    assignedCount: 0,
    completedCount: 0,
    completedOnTimeCount: 0,
  });

  let points: AssignedWorkStatsTimeseriesPoint[];
  if (params.fillBuckets !== false) {
    points = enumerateAssignedWorkBucketStarts(fromMs, toMs, bucket).map((start) => {
      const metrics = sparse.get(start) ?? emptyMetrics();
      return { bucket: new Date(start).toISOString(), ...metrics };
    });
  } else {
    points = [...sparse.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([start, metrics]) => ({
        bucket: new Date(start).toISOString(),
        ...metrics,
      }));
  }

  return { bucket, from: params.from, to: params.to, points };
}
